import { useNavigate, useSearch } from '@tanstack/react-router';
import { CircleCheckIcon, CircleXIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Loading } from '@/components/shared';
import { Button } from '@/components/ui/NewButton';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { useMatterLabels } from '@/lib/useMatterLabels';

export default function IManageAuthCallback() {
  const navigate = useNavigate();
  const {
    code,
    state,
    error: callbackError,
    error_description: errorDescription,
  } = useSearch({ from: '/imanage-auth-callback' });
  const [error, setError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(true);
  const [statusMessage, setStatusMessage] = useState('Exchanging authorization code...');
  const label = useMatterLabels();
  const labelRef = useRef(label);
  labelRef.current = label;

  useEffect(() => {
    const exchangeCodeForToken = async () => {
      if (callbackError) {
        setError(errorDescription || callbackError);
        setIsProcessing(false);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setIsProcessing(false);
        return;
      }

      try {
        setStatusMessage('Connecting to iManage...');

        // Exchange the code for an access token
        const response = await backendClient.post('/imanage/oauth/token', {
          code,
          state
        });

        setStatusMessage(labelRef.current('finalising-authentication'));

        if (response.data.access_token) {
          // Check if we have customer_id
          if (!response.data.customer_id) {
            setError('Authentication successful but customer information is missing. Please try again.');
            setIsProcessing(false);
            return;
          }

          // Store auth data in session storage
          const tokenIssuedAt = Date.now();
          const authData = {
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token || '',
            customer_id: response.data.customer_id,
            user_id: response.data.user_id || '',
            imanage_account_id: response.data.imanage_account_id,
            dialog_token: response.data.dialog_token,
            expires_in: response.data.expires_in || 3600,
            token_type: response.data.token_type || 'Bearer',
            token_issued_at: tokenIssuedAt
          };

          sessionStorage.setItem('imanage_auth', JSON.stringify(authData));
          setStatusMessage('Authentication complete! Loading file picker...');

          // Try to notify parent window if this was opened as popup
          if (window.opener && !window.opener.closed) {
            try {
              // Send the auth data to parent window
              window.opener.postMessage({
                type: 'imanage-auth-success',
                authData
              }, window.location.origin);

              // Wait for parent to acknowledge before closing
              let acknowledged = false;
              const handleAck = (event: MessageEvent) => {
                if (event.origin === window.location.origin && event.data.type === 'imanage-auth-acknowledged') {
                  acknowledged = true;
                  window.removeEventListener('message', handleAck);
                  setIsProcessing(false);
                  // Small delay to show success message
                  setTimeout(() => {
                    window.close();
                  }, 500);
                }
              };

              window.addEventListener('message', handleAck);

              // Fallback: close after 10 seconds if no acknowledgment
              setTimeout(() => {
                if (!acknowledged) {
                  window.removeEventListener('message', handleAck);
                  setIsProcessing(false);
                  window.close();
                }
              }, 10000);
            } catch {
              setIsProcessing(false);
              // If posting fails, redirect instead
              setTimeout(() => {
                navigate({ to: '/' });
              }, 2000);
            }
          } else {
            setIsProcessing(false);
            setTimeout(() => {
              navigate({ to: '/' });
            }, 2000);
          }
        } else {
          setError('Failed to exchange code for token');
          setIsProcessing(false);
        }
      } catch (err: unknown) {
        const message = apiErrorMessage(err) || (err instanceof Error ? err.message : 'Failed to authenticate');
        toast.error(message);
        setError(message);
        setIsProcessing(false);
      }
    };

    const timeoutId = setTimeout(() => {
      exchangeCodeForToken();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [callbackError, code, errorDescription, navigate, state]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="mb-4">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <CircleXIcon className="text-red-600" aria-hidden="true" />
            </div>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Authentication Failed</h2>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <Button type="button" icon={XIcon} text="Close Window" onClick={() => window.close()} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center max-w-md">
        {isProcessing && (
          <>
            <Loading />
            <h2 className="text-lg font-semibold text-gray-900">Completing iManage authentication...</h2>
            <p className="text-sm text-gray-600 mt-2">{statusMessage}</p>
            <p className="text-xs text-gray-500 mt-4">Please do not close this window</p>
          </>
        )}
        {!isProcessing && !error && (
          <>
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 mb-4">
              <CircleCheckIcon className="text-green-600" aria-hidden="true" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Authentication Successful!</h2>
            <p className="text-sm text-gray-600 mt-2">Loading iManage file picker...</p>
          </>
        )}
      </div>
    </div>
  );
}
